<template>
  <b-card-code title="">
    <div class="row mb-2">
      <div class="col-lg-6">
        <h4>Km/ l kontroling</h4>
      </div>
      <div class="col-lg-6 text-right">
        <button
          class="btn btn-primary"
          :disabled="loading"
          @click="openFilePicker"
        >
          {{ loading ? 'Feldolgozás...' : 'Import' }}
        </button>
        <input
          ref="fileInput"
          type="file"
          accept=".xls,.xlsx"
          multiple
          class="d-none"
          @change="onFileSelected"
        >
      </div>
    </div>

    <div class="row mb-2">
      <div class="col-lg-12">
        <p
          v-if="selectedFileName"
          class="mb-0"
        >
          Kiválasztott fájl(ok): <b>{{ selectedFileName }}</b>
        </p>
        <p
          v-else
          class="mb-0"
        >
          Nincs kiválasztott import fájl.
        </p>
      </div>
    </div>

    <div class="row mb-2">
      <div class="col-lg-3">
        <label class="mb-50">Dátumtól</label>
        <input
          v-model="filters.from"
          type="date"
          class="form-control"
          :disabled="loading"
        >
      </div>
      <div class="col-lg-3">
        <label class="mb-50">Dátumig</label>
        <input
          v-model="filters.to"
          type="date"
          class="form-control"
          :disabled="loading"
        >
      </div>
      <div class="col-lg-3">
        <label class="mb-50">Rendszám</label>
        <select
          v-model="filters.licensePlate"
          class="form-control"
          :disabled="loading"
        >
          <option value="">
            Összes
          </option>
          <option
            v-for="plate in trackedPlates"
            :key="`plate-filter-${plate.license_plate}`"
            :value="plate.license_plate"
          >
            {{ plate.license_plate }}
          </option>
        </select>
      </div>
      <div class="col-lg-3 d-flex align-items-end">
        <button
          class="btn btn-primary mr-1"
          :disabled="loading"
          @click="fetchRows"
        >
          Szűrés
        </button>
        <button
          class="btn btn-outline-secondary"
          :disabled="loading"
          @click="resetFilters"
        >
          Törlés
        </button>
      </div>
    </div>

    <div
      v-if="trackedPlates.length"
      class="row mb-2"
    >
      <div class="col-lg-12">
        <h5>Figyelt rendszámok</h5>
        <div class="d-flex flex-wrap">
          <label
            v-for="plate in trackedPlates"
            :key="`tracked-${plate.license_plate}`"
            class="mr-2 mb-1 border rounded px-1 py-1 d-flex align-items-center"
          >
            <input
              type="checkbox"
              class="mr-50"
              :checked="plate.is_tracked"
              :disabled="loading || savingPlate === plate.license_plate"
              @change="togglePlateTracking(plate, $event.target.checked)"
            >
            <span class="mr-50">{{ plate.license_plate }}</span>
            <select
              class="form-control form-control-sm"
              style="width: 72px;"
              :value="plate.vehicle_category || 'SZ'"
              :disabled="loading || savingPlate === plate.license_plate"
              @change="changePlateCategory(plate, $event.target.value)"
            >
              <option value="SZ">
                SZ
              </option>
              <option value="TGK">
                TGK
              </option>
            </select>
          </label>
        </div>
      </div>
    </div>

    <div
      v-if="summary.length"
      class="row mb-2"
    >
      <div class="col-lg-12">
        <h5>Jármű összesítő</h5>
        <div class="table-responsive">
          <table class="table table-sm table-striped">
            <thead>
              <tr>
                <th>Rendszám</th>
                <th>Valid minta</th>
                <th>Átlag fogyasztás (l/100km)</th>
                <th>Átlag futás (km/nap)</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in summary"
                :key="`summary-${row.license_plate}`"
              >
                <td>{{ row.license_plate }}</td>
                <td>{{ row.valid_count }}</td>
                <td>{{ formatNumber(row.avg_l_100km) }}</td>
                <td>{{ formatNumber(row.avg_km_day) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-12">
        <h5>Tankolási sorok</h5>
        <div class="table-responsive">
          <table class="table table-sm table-hover">
            <thead>
              <tr>
                <th>Dátum</th>
                <th>Rendszám</th>
                <th>Termék</th>
                <th>Liter</th>
                <th>Km óra</th>
                <th>Figyelt</th>
                <th>Km diff</th>
                <th>Nap diff</th>
                <th>Átlag (l/100)</th>
                <th>Átlag (km/nap)</th>
                <th>Státusz</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in rows"
                :key="row.id"
              >
                <td>{{ row.event_at }}</td>
                <td>{{ row.license_plate }}</td>
                <td>{{ row.product_name || '-' }}</td>
                <td>{{ formatNumber(row.quantity_liter) }}</td>
                <td>{{ formatInteger(row.odometer_km) }}</td>
                <td>
                  <span
                    v-if="row.is_tracked"
                    class="badge badge-success"
                  >Igen</span>
                  <span
                    v-else
                    class="badge badge-secondary"
                  >Nem</span>
                </td>
                <td>{{ formatInteger(row.delta_km) }}</td>
                <td>{{ formatNumber(row.delta_days) }}</td>
                <td>{{ formatNumber(row.avg_l_100km) }}</td>
                <td>{{ formatNumber(row.avg_km_day) }}</td>
                <td>
                  <span
                    v-if="row.included_in_calc"
                    class="badge badge-success"
                  >Valid</span>
                  <span
                    v-else-if="row.is_base_reset"
                    class="badge badge-warning"
                    :title="row.invalid_reason || ''"
                  >Új alap</span>
                  <span
                    v-else-if="row.is_tracked === false"
                    class="badge badge-secondary"
                  >Nem figyelt</span>
                  <span
                    v-else-if="row.is_valid === false"
                    class="badge badge-danger"
                    :title="row.invalid_reason || ''"
                  >Invalid</span>
                  <span
                    v-else
                    class="badge badge-secondary"
                  >Alap sor</span>
                </td>
              </tr>
              <tr v-if="!rows.length">
                <td
                  colspan="11"
                  class="text-center"
                >
                  Nincs importált adat.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </b-card-code>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  data() {
    return {
      selectedFileName: '',
      selectedFiles: [],
      loading: false,
      rows: [],
      summary: [],
      trackedPlates: [],
      savingPlate: '',
      filters: {
        from: '',
        to: '',
        licensePlate: '',
      },
    }
  },
  mounted() {
    this.fetchRows()
  },
  methods: {
    openFilePicker() {
      this.$refs.fileInput.click()
    },
    async onFileSelected(event) {
      const files = Array.from(event.target.files || [])
      this.selectedFiles = files
      this.selectedFileName = files.length
        ? files.map(file => file.name).join(', ')
        : ''

      if (!this.selectedFiles.length) {
        return
      }

      this.loading = true

      try {
        const tasks = this.selectedFiles.map(file => {
          const formData = new FormData()
          formData.append('file', file)

          return axios({
            method: 'post',
            url: 'km-l-controlling/import',
            data: formData,
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data',
            },
          })
            .then(response => ({
              imported: Number(response?.data?.imported || 0),
              skipped: Number(response?.data?.skipped || 0),
              failed: 0,
            }))
            .catch(() => ({
              imported: 0,
              skipped: 0,
              failed: 1,
            }))
        })
        const results = await Promise.all(tasks)
        const totalImported = results.reduce((sum, item) => sum + item.imported, 0)
        const totalSkipped = results.reduce((sum, item) => sum + item.skipped, 0)
        const failed = results.reduce((sum, item) => sum + item.failed, 0)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: `Import kész: ${totalImported} sor, kihagyva: ${totalSkipped}, hibás fájl: ${failed}`,
            icon: 'CheckCircleIcon',
            variant: failed > 0 ? 'warning' : 'success',
          },
        })

        await this.fetchRows()
      } finally {
        this.loading = false
        this.$refs.fileInput.value = ''
        this.selectedFiles = []
      }
    },
    async fetchRows() {
      try {
        const response = await axios({
          method: 'get',
          url: 'km-l-controlling/list',
          params: {
            from: this.filters.from || undefined,
            to: this.filters.to || undefined,
            license_plate: this.filters.licensePlate || undefined,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        })

        this.rows = response.data.items || []
        this.summary = response.data.summary || []
        this.trackedPlates = response.data.tracked_plates || []
      } catch (error) {
        const message = error?.response?.data?.message || 'Adatok lekérése sikertelen'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    resetFilters() {
      this.filters.from = ''
      this.filters.to = ''
      this.filters.licensePlate = ''
      this.fetchRows()
    },
    async togglePlateTracking(plate, isTracked) {
      this.savingPlate = plate.license_plate
      try {
        await axios({
          method: 'put',
          url: 'km-l-controlling/tracking',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          data: {
            license_plate: plate.license_plate,
            is_tracked: isTracked,
          },
        })
        await this.fetchRows()
      } catch (error) {
        const message = error?.response?.data?.message || 'Rendszám beállítás mentése sikertelen'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.savingPlate = ''
      }
    },
    async changePlateCategory(plate, category) {
      this.savingPlate = plate.license_plate
      try {
        await axios({
          method: 'put',
          url: 'km-l-controlling/tracking',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          data: {
            license_plate: plate.license_plate,
            vehicle_category: category,
          },
        })
        await this.fetchRows()
      } catch (error) {
        const message = error?.response?.data?.message || 'Rendszám kategória mentése sikertelen'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.savingPlate = ''
      }
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      const num = Number(value)
      if (Number.isNaN(num)) {
        return '-'
      }
      return num.toLocaleString('hu-HU', { maximumFractionDigits: 2 })
    },
    formatInteger(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      const num = Number(value)
      if (Number.isNaN(num)) {
        return '-'
      }
      return Math.round(num).toLocaleString('hu-HU')
    },
  },
}
</script>
